import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  AccountBalanceWallet,
  People,
  Inventory,
  Link as LinkIcon,
} from '@mui/icons-material';
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   ResponsiveContainer,
// } from 'recharts';
import { useNavigate } from 'react-router-dom';
import {
  getPartnerDashboard,
  getStripeConnectStatus,
  startStripeOnboarding,
} from '../../services/api/partnerService';
import { PartnerDashboardSummary, StripeConnectStatus } from '../../types';

const formatCurrency = (amount: number): string => {
  return `¥${amount.toLocaleString()}`;
};

interface SummaryCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: number;
  color?: string;
}

const SummaryCard = ({ title, value, subtitle, icon, trend, color = '#1976d2' }: SummaryCardProps) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
          {trend !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              {trend >= 0 ? (
                <TrendingUp sx={{ color: 'success.main', fontSize: 20, mr: 0.5 }} />
              ) : (
                <TrendingDown sx={{ color: 'error.main', fontSize: 20, mr: 0.5 }} />
              )}
              <Typography
                variant="body2"
                sx={{ color: trend >= 0 ? 'success.main' : 'error.main' }}
              >
                {trend >= 0 ? '+' : ''}{trend.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                前月比
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            bgcolor: `${color}15`,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const PartnerDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<PartnerDashboardSummary | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboardData, stripeData] = await Promise.all([
        getPartnerDashboard(),
        getStripeConnectStatus(),
      ]);
      setDashboard(dashboardData);
      setStripeStatus(stripeData);
    } catch (err: any) {
      console.error('Dashboard load error:', err);
      setError(err.response?.data?.detail || 'データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    setOnboardingLoading(true);
    try {
      const response = await startStripeOnboarding(
        `${window.location.origin}/partner/dashboard`,
        `${window.location.origin}/partner/dashboard`
      );
      window.location.href = response.onboarding_url;
    } catch (err: any) {
      console.error('Onboarding error:', err);
      setError(err.response?.data?.detail || 'Stripeオンボーディングの開始に失敗しました');
    } finally {
      setOnboardingLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadData}>
          再読み込み
        </Button>
      </Box>
    );
  }

  if (!dashboard) return null;

  const planLabel = dashboard.plan === 'unlimited' ? 'Unlimited' : 'Standard';
  const planColor = dashboard.plan === 'unlimited' ? '#9c27b0' : '#1976d2';
  const productUsage = (dashboard.enabled_products / dashboard.max_products) * 100;

  return (
    <Box sx={{ p: 3 }}>
      {/* ヘッダー */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            パートナーダッシュボード
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={`${planLabel}プラン`}
              sx={{ bgcolor: planColor, color: 'white' }}
            />
            <Typography variant="body2" color="text.secondary">
              手数料: {dashboard.fee_percent}%
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stripe Connect警告 */}
      {stripeStatus && !stripeStatus.onboarding_complete && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleStartOnboarding}
              disabled={onboardingLoading}
            >
              {onboardingLoading ? <CircularProgress size={20} /> : '設定する'}
            </Button>
          }
        >
          Stripe Connectの設定が完了していません。売上を受け取るには設定を完了してください。
        </Alert>
      )}

      {/* サマリーカード */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="今月の売上"
            value={formatCurrency(dashboard.total_sales_this_month)}
            trend={dashboard.sales_growth_percent}
            icon={<ShoppingCart sx={{ fontSize: 32, color: '#1976d2' }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="あなたの収益"
            value={formatCurrency(dashboard.partner_revenue_this_month)}
            subtitle={`売上の${100 - dashboard.fee_percent}%`}
            icon={<AccountBalanceWallet sx={{ fontSize: 32, color: '#2e7d32' }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="総顧客数"
            value={dashboard.total_customers.toLocaleString()}
            subtitle={`有料: ${dashboard.paid_customers} / 無料: ${dashboard.free_customers}`}
            icon={<People sx={{ fontSize: 32, color: '#ed6c02' }} />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="有効化商品"
            value={`${dashboard.enabled_products} / ${dashboard.max_products}`}
            icon={<Inventory sx={{ fontSize: 32, color: '#9c27b0' }} />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* 商品使用状況 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                商品使用状況
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {dashboard.enabled_products} / {dashboard.max_products} 商品
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {productUsage.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(productUsage, 100)}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      bgcolor: productUsage >= 100 ? 'error.main' : 'primary.main',
                    },
                  }}
                />
              </Box>
              {dashboard.plan === 'standard' && productUsage >= 80 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  商品数の上限に近づいています。Unlimitedプランにアップグレードすると無制限で利用できます。
                </Alert>
              )}
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => navigate('/partner/products')}
                startIcon={<Inventory />}
              >
                商品を管理
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                クイックアクション
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate('/partner/payment-links')}
                    startIcon={<LinkIcon />}
                    sx={{ py: 1.5 }}
                  >
                    決済リンク
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate('/partner/revenue')}
                    startIcon={<AccountBalanceWallet />}
                    sx={{ py: 1.5 }}
                  >
                    売上レポート
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate('/partner/customers')}
                    startIcon={<People />}
                    sx={{ py: 1.5 }}
                  >
                    顧客管理
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate('/partner/branding')}
                    sx={{ py: 1.5 }}
                  >
                    ブランディング
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 収益シミュレーション */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            収益の内訳（今月）
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                総売上
              </Typography>
              <Typography variant="h5">
                {formatCurrency(dashboard.total_sales_this_month)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h5" color="text.secondary">-</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                プラットフォーム手数料 ({dashboard.fee_percent}%)
              </Typography>
              <Typography variant="h5" color="error.main">
                {formatCurrency(Math.round(dashboard.total_sales_this_month * dashboard.fee_percent / 100))}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h5" color="text.secondary">=</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                あなたの収益 ({100 - dashboard.fee_percent}%)
              </Typography>
              <Typography variant="h5" color="success.main">
                {formatCurrency(dashboard.partner_revenue_this_month)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PartnerDashboardPage;
